<template>
  <div data-testid="page-dashboard">
    <h1>Dashboard</h1>
    <div v-if="canViewReviewsAd" ref="adWrapper" class="ad-wrapper mt-4">
      <ins class="77423bc4" data-key="ff9b88f44185101e788f50e3846df4f2"></ins>
    </div>
    <AlertBox v-if="!hasPartnerCompany" type="warning" class="mt-5">
      {{ t('PartnerCompany.noPartnerCompany') }}
    </AlertBox>
    <TopProductsRankingDashboardBlock v-if="hasPartnerCompany && canManageReviews" />

    <NotificationFeed v-if="me && hasPartnerCompany && canSeeNotificationFeed" />

    <div v-if="canViewTickets" class="mt-10">
      <h2>{{ t('Dashboard.quotas.title') }}</h2>
      <p class="mt-2">
        <i18n-t keypath="Dashboard.quotas.body" tag="span">
          <template #ticketsLink>
            <RouterLink :to="{ name: 'tickets' }">{{ t('Dashboard.quotas.ticketsLink') }}</RouterLink>
          </template>
        </i18n-t>
      </p>
    </div>

    <div v-if="me && hasPartnerCompany" class="dashboard">
      <DueDates />

      <!-- News section -->
      <NewsList v-if="result?.news?.nodes?.length" :news="result.news.nodes" />

      <!-- Forced news popup -->
      <NewsModal
        v-if="result && showForcedNews && forcedNews.length"
        :news-item="forcedNews[currentForcedNewsItem]"
        @closed-modal="displayNextNewsItem"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { DueDates, NewsList } from '@/components'
import { NewsModal, NotificationFeed } from '@/components/Dashboard'
import { TopProductsRankingDashboardBlock } from '@/components/ReviewsManagement/TopProductsRanking'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { useNewsQuery, type MeQuery } from '@/gql/myomr'
import { $can } from '@/services/roles'
import { useMeStore } from '@/stores'

const { t } = useI18n()

const meStore = useMeStore()

const props = defineProps<{
  me: NonNullable<MeQuery['me']>
}>()

const hasPartnerCompany = computed(() => meStore.hasCurrentPartnerCompany)
const { enabled: isNotificationFeedEnabled } = useFeatureFlag(FEATURE_FLAG.NOTIFICATIONS.FEED_RELEASE)
const canManageReviews = computed(() => $can('manageReviews', { for: 'User' }))
const canViewReviewsAd = computed(() => $can('viewReviewsAd', { for: 'User' }))
const canSeeNotificationFeed = computed(() => isNotificationFeedEnabled.value && canManageReviews.value)

const newsQueryEnabled = computed(() => !!props.me && hasPartnerCompany.value)
const { result, refetch } = useNewsQuery({ enabled: newsQueryEnabled })
const canViewTickets = computed(() => $can('viewTickets', { for: 'User' }))

const showForcedNews = ref(false)
const currentForcedNewsItem = ref(0)

const forcedNews = computed(() => result.value?.news.nodes.filter((newsItem) => newsItem.force) || [])

const adWrapper = useTemplateRef('adWrapper')
const adScriptLoaded = ref(false)

function loadAdScript() {
  if (adScriptLoaded.value || !adWrapper.value) {
    return
  }

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://cdn77.omr.com/f8e6c5eb.js'

  adWrapper.value.appendChild(script)
  adScriptLoaded.value = true
}

watch(adWrapper, loadAdScript, { immediate: true })

onMounted(() => {
  if (window.localStorage.getItem('showForcedNews') !== 'false') {
    showForcedNews.value = true
  }
})

function displayNextNewsItem() {
  if (currentForcedNewsItem.value + 1 >= forcedNews.value.length) {
    window.localStorage.setItem('showForcedNews', 'false')
    showForcedNews.value = false
    return
  }
  currentForcedNewsItem.value += 1
}

watch(
  () => props.me.newsTags,
  () => refetch(),
  { deep: true },
)
</script>
<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';
/* we need this for scaling because epom has no scaling at the moment */
.ad-wrapper :deep(img) {
  @apply max-w-full!;
  @apply rounded-md!;
}
</style>
